/**
 * 全編モーション (docs/features/brand-cut-motion-graphics-plan.md §10.5) のシナリオで、ブランドカットのシーンを
 * モーショングラフィックスで作る入口。BrandCutModal を「作り方 = モーション」既定で開き、計画 (motion_plan) に
 * このシーンの item があれば尺を計画の値 (小数) に固定して scene_id と一緒に送る (成果物に計画の版が付き、以降の
 * 本編の計画が有効なまま)。完了したらそのシーンの動画に紐づける。
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../services/apiClient';
import { useToast } from '../../../hooks/useToast';
import { useGenerationJobs } from '../state/jobsContext';
import BrandCutModal, { type BrandCutModalLogo } from '../../BrandCutModal';
import {
  createBrandCutGenerationJob,
  waitForJobCompletion,
  type BrandCutGenerationParams,
  type BrandCutGenerationResult,
} from '../../../services/asyncJobApi';
import { useScenarioProductBrandInfo } from '../../../features/scenarios/hooks';
import type { Brand, BrandLogo } from '../../../features/brands/types';
import { useSceneHandlers } from './hooks/SceneHandlersContext';
import { useMotionPlan } from './hooks/useMotionPlan';
import { findMotionBrandCutPlanItem } from './motionScenario';
import { motionJobProgressPatch } from '../../../features/motion/jobs';
import type { Scenario } from './types';

export function MotionBrandCutButton({ scenario, scene }: { scenario: Scenario; scene: Scenario['scenes'][number] }) {
  const toast = useToast();
  const { setActiveJobs } = useGenerationJobs();
  const { handleSelectVideo } = useSceneHandlers();
  const [open, setOpen] = useState(false);
  const { data: plan } = useMotionPlan(scenario);
  const planItem = findMotionBrandCutPlanItem(plan, scene.id);
  const { data: brandInfo } = useScenarioProductBrandInfo(scenario.id);
  const brandId = brandInfo?.current_brand_id ?? null;
  const { data: brand } = useQuery<Brand>({
    queryKey: ['brand', brandId],
    queryFn: async () => (await apiClient.get(`/brands/${brandId}`)).data.brand,
    enabled: open && !!brandId,
  });
  const logos: BrandCutModalLogo[] = (brand?.logos ?? []).map((l: BrandLogo) => ({
    id: l.id,
    description: l.description,
    background: l.background,
    color_type: l.color_type,
    layout: l.layout,
    is_default: l.is_default,
    logos: l.logos ? { url: l.logos.url, file_name: l.logos.file_name } : undefined,
  }));

  const key = `${scenario.id}-scene-${scene.order}`;
  const label = scene.brand_cut_position === 'start' ? 'タイトルカード' : 'エンドカード';

  const launch = async (params: BrandCutGenerationParams) => {
    try {
      const { job_id } = await createBrandCutGenerationJob(params);
      setActiveJobs((prev) => [
        ...prev,
        { jobId: job_id, status: 'polling' as const, startedAt: Date.now(), type: 'video' as const, label: `${label} (モーション)` },
      ]);
      waitForJobCompletion<BrandCutGenerationResult>(job_id, {
        skipVisibilityCheck: true,
        // 段階表示と、こだわり生成の候補 / 中断の受付を生成キューの行に写す (§10.10.1)
        onProgress: (jobInfo) => {
          setActiveJobs((prev) => prev.map((j) => (j.jobId === job_id ? { ...j, ...motionJobProgressPatch(jobInfo) } : j)));
        },
      })
        .then((job) => {
          const result = job.result_data;
          setActiveJobs((prev) => prev.map((j) => (j.jobId === job_id
            ? { ...j, status: 'completed' as const, result: { id: job_id, material_id: result?.material_id ?? undefined, image_url: result?.thumbnail_url ?? undefined } }
            : j)));
          if (result?.material_id) {
            const seconds = typeof params.duration_seconds === 'number' ? params.duration_seconds : undefined;
            handleSelectVideo(scene.id, result.material_id, key, seconds, true);
          }
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'ブランドカットの生成に失敗しました';
          setActiveJobs((prev) => prev.map((j) => (j.jobId === job_id ? { ...j, status: 'failed' as const, error: msg } : j)));
        });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'ブランドカットの生成を受け付けられませんでした');
    }
  };

  return (
    <>
      <button
        type="button"
        disabled={!brandId}
        title={brandId
          ? (planItem ? `BGM に割り付けた尺 (${planItem.sec} 秒) でモーションのブランドカットを作ります` : 'モーションでブランドカットを作ります')
          : 'シナリオの商品にブランドが紐づいていないため作れません'}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        style={{
          fontSize: 12, color: 'rgba(245, 241, 230, 0.6)', background: 'none', border: 'none', padding: 0,
          textDecoration: 'underline', cursor: brandId ? 'pointer' : 'default', opacity: brandId ? 1 : 0.5, whiteSpace: 'nowrap',
        }}
      >
        モーションで作る{planItem ? ` (${planItem.sec} 秒)` : ''}
      </button>
      {brandId && (
        <BrandCutModal
          isOpen={open && !!brand}
          onClose={() => setOpen(false)}
          brandId={brandId}
          brandName={brand?.brand_name ?? ''}
          tagline={brand?.tagline}
          description={brand?.description}
          brandMainColor={brand?.brand_main_color}
          brandSubColor={brand?.brand_sub_color}
          brandAccentColor={brand?.brand_accent_color}
          logos={logos}
          onSubmit={(params) => { void launch(params); }}
          defaultEngine="motion"
          defaultAspectRatio={scenario.aspect_ratio}
          plannedMotionSeconds={planItem?.sec ?? null}
          sceneId={planItem ? scene.id : undefined}
        />
      )}
    </>
  );
}
